// The DC size totals over the dashboard rows in view: proposed (the platform's figure),
// committed and achievable, each saying how many of the sites it covers.
import type { DashboardRow } from "../api/hooks/useDashboard";
import { achievableKwp } from "../utils/achievableKwp";
import { smallestFigure } from "../utils/attributeFormat";
import styles from "./DashboardTotals.module.css";

type Kwp = (row: DashboardRow) => number | undefined;

const figure = (key: string): Kwp => (row) => {
  const value = smallestFigure(row.attributes[key]);
  return typeof value === "number" ? value : undefined;
};

const proposed: Kwp = (row) => row.pitched["committed_kwp"]?.value;
const committed = figure("committed_kwp");
const achievable: Kwp = (row) => achievableKwp(row)?.kwp;

const mwp = (kwp: number): string =>
  (kwp / 1000).toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const sites = (n: number): string => `${n} site${n === 1 ? "" : "s"}`;

const Tile = ({
  label,
  rows,
  read,
  verb,
  inferred = 0,
  shortfall,
}: {
  label: string;
  rows: DashboardRow[];
  read: Kwp;
  verb: string;
  // How many of the figures are inferred from grid kWac.
  inferred?: number;
  shortfall?: { mwp: number; sites: number };
}) => {
  const figures = rows.map(read).filter((v): v is number => v !== undefined);
  const total = figures.reduce((a, b) => a + b, 0);
  const coverage =
    rows.length === 0 ? "no site matches" : `${figures.length} of ${sites(rows.length)}`;
  const note = [
    rows.length === 0
      ? "No site matches the filters"
      : figures.length === rows.length
        ? `Across ${coverage}`
        : `${coverage} ${verb}; ${rows.length - figures.length} not set`,
    inferred > 0 &&
      `${sites(inferred)} counted at a kWp inferred from grid kWac`,
    shortfall &&
      `${mwp(shortfall.mwp)} MWp below committed on the ${sites(shortfall.sites)} short of it`,
  ]
    .filter(Boolean)
    .join(". ");
  return (
    <div className={styles.tile} title={note}>
      <span className={styles.label}>{label}</span>
      <span className={styles.value}>
        {figures.length ? (
          <>
            {mwp(total)} <span className={styles.unit}>MWp</span>
          </>
        ) : (
          <span className={styles.unset}>not set</span>
        )}
      </span>
      <span className={styles.coverage}>
        {coverage}
        {inferred > 0 && <em>, {inferred} inferred</em>}
      </span>
      {shortfall && (
        <span className={styles.short}>
          -{mwp(shortfall.mwp)} vs committed
          {shortfall.sites < rows.length && ` on ${sites(shortfall.sites)}`}
        </span>
      )}
    </div>
  );
};

export const DashboardTotals = ({ rows }: { rows: DashboardRow[] }) => {
  // Only the sites short of committed: one above it is not offset against another below.
  const short = rows.filter(
    (r) =>
      committed(r) !== undefined &&
      achievable(r) !== undefined &&
      (achievable(r) as number) < (committed(r) as number),
  );
  const gap = short.reduce((a, r) => a + (committed(r) ?? 0) - (achievable(r) ?? 0), 0);
  // Under 5 kWp would read as "-0.00".
  const shortfall = Math.round(gap / 10) > 0 ? { mwp: gap, sites: short.length } : undefined;
  return (
    <section
      className={`${styles.totals} ${rows.length === 0 ? styles.disabled : ""}`}
      aria-label="DC size totals"
      aria-disabled={rows.length === 0}
    >
      <Tile label="Proposed" rows={rows} read={proposed} verb="proposed" />
      <Tile label="Committed" rows={rows} read={committed} verb="committed" />
      <Tile
        label="Achievable"
        rows={rows}
        read={achievable}
        verb="assessed"
        inferred={rows.filter((r) => achievableKwp(r)?.inferred).length}
        shortfall={shortfall}
      />
    </section>
  );
};
